import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Bot,
  Plug,
  Sparkles,
  CalendarClock,
  BarChart3,
  Send,
  MessageSquareText,
  Check,
  ArrowRight,
  Pencil,
  Ban,
  Megaphone,
  Repeat2,
  ListChecks,
  Lightbulb,
  Copy,
} from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { SiteFooter } from '../components/SiteFooter';
import { useSeo } from '../lib/seo';

const MCP_URL = 'https://zettapost.com/mcp';

const STEPS: { title: string; body: string }[] = [
  {
    title: 'Abre los conectores de ChatGPT',
    body: 'En ChatGPT ve a Configuración → Conectores → Crear (o "Nueva aplicación"). También funciona en Claude, Cursor y otros clientes compatibles con MCP.',
  },
  {
    title: 'Pega la URL del servidor',
    body: `Usa ${MCP_URL} como URL del servidor MCP y elige autenticación OAuth.`,
  },
  {
    title: 'Método de registro: DCR',
    body: 'Deja "Registro dinámico de clientes (DCR)". No necesitas crear ni pegar ningún token: el conector se registra solo.',
  },
  {
    title: 'Inicia sesión y autoriza',
    body: 'Se abrirá el login de Zettapost. Inicia sesión y pulsa "Autorizar". ¡Ya está conectado a tu cuenta!',
  },
];

const PROMPTS: { icon: typeof Send; label: string; prompt: string }[] = [
  {
    icon: Send,
    label: 'Publicar al instante',
    prompt: 'Publica ahora en Instagram y X: «Nuevo artículo en el blog 🎉 Cómo programar tus redes en 5 minutos». Añade la imagen https://…/portada.jpg',
  },
  {
    icon: CalendarClock,
    label: 'Programar',
    prompt: 'Programa este post en LinkedIn para el viernes a las 10:00 (hora de Madrid): «3 errores que cometen las marcas en redes…».',
  },
  {
    icon: BarChart3,
    label: 'Analíticas',
    prompt: '¿Cómo han ido mis redes los últimos 30 días? Dame likes, alcance e interacción y mis 3 mejores publicaciones.',
  },
  {
    icon: MessageSquareText,
    label: 'Texto por red',
    prompt: 'Coge esta idea y adáptala: versión con emojis y hashtags para Instagram, más profesional para LinkedIn, y prográmalas mañana.',
  },
  {
    icon: Bot,
    label: 'Tus cuentas',
    prompt: '¿Qué cuentas de redes tengo conectadas en Zettapost?',
  },
  {
    icon: Sparkles,
    label: 'Plan de contenido',
    prompt: 'Proponme 5 publicaciones para esta semana sobre productividad y prográmalas en Instagram repartidas de lunes a viernes.',
  },
  {
    icon: Pencil,
    label: 'Editar',
    prompt: 'Cambia el texto de mi último post programado y muévelo al sábado a las 12:00 (hora de Madrid).',
  },
  {
    icon: Ban,
    label: 'Cancelar',
    prompt: 'Cancela el post que tengo programado para mañana en X.',
  },
  {
    icon: ListChecks,
    label: 'Ver programados',
    prompt: '¿Qué tengo programado esta semana? Lístalo por día con la red y la hora.',
  },
  {
    icon: Megaphone,
    label: 'Multi-red',
    prompt: 'Publica este anuncio en todas mis cuentas a la vez: «Esta semana 20% de descuento en todos los planes 🎉».',
  },
  {
    icon: Repeat2,
    label: 'Reaprovechar',
    prompt: 'Convierte este artículo del blog en un carrusel para Instagram y un hilo para X, y prográmalos para el lunes.',
  },
  {
    icon: Lightbulb,
    label: 'Lluvia de ideas',
    prompt: 'Dame 10 ideas de publicaciones sobre marketing para pymes y programa las 3 mejores en LinkedIn esta semana.',
  },
];

const CAPABILITIES = [
  'Publicar de inmediato en cualquier red conectada',
  'Programar publicaciones para una fecha y hora',
  'Adaptar el texto por red social',
  'Consultar analíticas y mejores publicaciones',
  'Ver tus cuentas conectadas',
  'Comprobar el estado de una publicación',
];

function PromptCard({ icon: Icon, label, prompt }: { icon: typeof Send; label: string; prompt: string }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(prompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard not available */
    }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm flex flex-col">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="inline-flex items-center gap-2 text-xs font-semibold text-brand-600">
          <Icon size={14} /> {label}
        </div>
        <button
          type="button"
          onClick={copy}
          className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium transition-colors ${
            copied ? 'text-green-600' : 'text-slate-400 hover:text-brand-600 hover:bg-slate-100'
          }`}
          aria-label="Copiar prompt"
        >
          {copied ? <Check size={13} /> : <Copy size={13} />}
          {copied ? 'Copiado' : 'Copiar'}
        </button>
      </div>
      <p className="text-sm text-slate-700 leading-relaxed">“{prompt}”</p>
    </div>
  );
}

export function ChatGptPage() {
  useSeo({
    title: 'Conecta Zettapost con ChatGPT (MCP) · Publica y mide tus redes desde la IA',
    description:
      'Conecta Zettapost a ChatGPT, Claude o Cursor con nuestro conector MCP y gestiona tus redes hablando con la IA: publica, programa y consulta analíticas. Sin pegar tokens.',
    canonical: 'https://zettapost.com/chatgpt',
  });

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-5xl mx-auto px-6 pt-20 pb-14 text-center">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-brand-600 bg-brand-50 border border-brand-100 rounded-full px-3 py-1">
            <Bot size={14} /> Conector MCP
          </span>
          <h1 className="mt-5 text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 leading-tight">
            Gestiona tus redes <span className="text-brand-600">hablando con ChatGPT</span>
          </h1>
          <p className="mt-4 text-lg text-slate-600 max-w-2xl mx-auto">
            Conecta Zettapost a ChatGPT (o Claude, Cursor…) y publica, programa y mide tus redes
            sociales con lenguaje natural. Tú lo pides, la IA lo hace en tu cuenta.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <a href="#conectar" className="btn-primary gap-2">
              <Plug size={16} /> Cómo conectarlo
            </a>
            <Link to="/register" className="btn-secondary">Crear cuenta gratis</Link>
          </div>
          <p className="mt-3 text-xs text-slate-400">Incluido en todos los planes activos</p>
        </div>
      </section>

      {/* What is it */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-5xl mx-auto px-6 py-16 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">¿Qué es el conector MCP?</h2>
            <p className="mt-3 text-slate-600">
              MCP (Model Context Protocol) es el estándar que permite a asistentes como ChatGPT o Claude
              usar herramientas externas de forma segura. Nuestro conector le da a tu IA acceso —con tu
              permiso— a tu cuenta de Zettapost.
            </p>
            <p className="mt-3 text-slate-600">
              En vez de entrar al panel, le hablas a la IA en lenguaje natural y ella publica, programa o
              te resume tus métricas. La conexión usa <strong>OAuth</strong>: inicias sesión con tu cuenta y
              autorizas el acceso, sin copiar ni pegar claves.
            </p>
          </div>
          <ul className="space-y-2.5">
            {CAPABILITIES.map((c) => (
              <li key={c} className="flex items-start gap-2.5 text-slate-700">
                <span className="mt-0.5 h-5 w-5 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0">
                  <Check size={13} />
                </span>
                {c}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* How to connect */}
      <section id="conectar" className="scroll-mt-20">
        <div className="max-w-4xl mx-auto px-6 py-16">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">Conéctalo en 4 pasos</h2>
            <p className="mt-2 text-slate-600">Menos de un minuto. No necesitas saber programar.</p>
          </div>

          <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 flex items-center gap-3 shadow-sm">
            <span className="text-xs font-medium text-slate-500 shrink-0">URL del servidor</span>
            <code className="flex-1 text-sm font-mono text-slate-800 truncate">{MCP_URL}</code>
          </div>

          <ol className="mt-6 space-y-4">
            {STEPS.map((s, i) => (
              <li key={i} className="flex gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <span className="shrink-0 h-8 w-8 rounded-full bg-brand-600 text-white font-semibold flex items-center justify-center">
                  {i + 1}
                </span>
                <div>
                  <h3 className="font-semibold text-slate-900">{s.title}</h3>
                  <p className="mt-1 text-sm text-slate-600">{s.body}</p>
                </div>
              </li>
            ))}
          </ol>

          <div className="mt-6 rounded-2xl border border-brand-200 bg-brand-50 p-5 sm:p-6 text-center">
            <span className="inline-flex items-center rounded-full bg-brand-600/10 px-3 py-1 text-xs font-semibold text-brand-700">
              Próximamente
            </span>
            <p className="mt-3 text-sm sm:text-base text-slate-700 leading-relaxed">
              Estamos trabajando con <strong>OpenAI</strong> para que Zettapost sea una aplicación pública:
              podrás instalarla buscando <strong>“Zettapost”</strong> en la tienda de apps de ChatGPT, sin pegar ninguna URL.
            </p>
          </div>
        </div>
      </section>

      {/* Example prompts */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-5xl mx-auto px-6 py-16">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">Qué pedirle a tu IA</h2>
            <p className="mt-2 text-slate-600">Ejemplos de prompts que puedes copiar y adaptar.</p>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {PROMPTS.map((p) => (
              <PromptCard key={p.label} {...p} />
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section>
        <div className="max-w-3xl mx-auto px-6 py-20 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">Crea, programa y publica desde ChatGPT{' '}<br />con Zettapost</h2>
          <p className="mt-3 text-slate-600">
            Crea tu cuenta, conecta tus redes y empieza a gestionarlas desde ChatGPT hoy mismo.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/register" className="btn-primary gap-2">
              Empezar gratis <ArrowRight size={16} />
            </Link>
            <Link to="/dashboard/api" className="btn-secondary">Ver guía en el panel</Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <SiteFooter />
    </div>
  );
}
